// Measurement: the site's Google Analytics 4 events, in one place (docs/ANALYTICS.md lists every event and its params).
// The site (app.js, ward-alerts.js), the dashboard's public pages (dashboard-src/lib/analytics.ts) and the Worker's own
// small pages (worker/src/analytics-tag.js) all go through these functions, so every page view carries the same content
// group and every event is cleaned the same way.
//
// Never sent: anything a reader typed (addresses, emails, names, search phrases), and no address in a page's URL. Ward
// numbers, public record ids and slugs are fine.

// Route → content group (GA4's built-in "Content group" dimension). First match wins; anything else is "Other".
export const CONTENT_GROUPS = [
  [/^\/$|^\/(?:find|alderman)$/, 'Home'],
  [/^\/(?:ward|district)s?(?:\/|$)|^\/former\//, 'Ward'],
  [/^\/(?:legislation|tracker)$|^\/(?:matter|vote)\//, 'Legislation'],
  [/^\/committees?(?:\/|$)/, 'Committees'],
  [/^\/meetings?(?:\/|$)|^\/agendas$|^\/mayor\/videos\//, 'Meetings'],
  [/^\/elections(?:\/|$)|^\/candidate\//, 'Election'],
  [/^\/on-my-block(?:\/|$)/, 'On My Block'],
  [/^\/(?:money|funding|campaign-finance|lobbying|connections)(?:\/|$)|^\/(?:lobbyist|entity)\//, 'Money'],
  [/^\/(?:insights|network)(?:\/|$)/, 'Insights'],
  [/^\/research(?:\/|$)/, 'Research'],
  [/^\/(?:signin|account|alerts)(?:\/|$)|^\/dashboard(?:\/|$)/, 'Account'],
  [/^\/(?:about|donate|pricing|developers|sitemap|contact|privacy|terms|editorial-standards)(?:\/|$)|^\/for\//, 'About'],
];

export function contentGroup(path) {
  const p = String(path || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  for (const [re, group] of CONTENT_GROUPS) if (re.test(p)) return group;
  return 'Other';
}

// The ward a page is about (/ward/12, its money pages, its 2027 race), or null. (One URL per page in every language.)
export function pageWard(path) {
  const m = String(path || '').match(/^\/(?:(?:ward|district)|(?:funding|campaign-finance)\/district|elections\/\d{4}\/ward)\/(\d{1,2})(?:[/?#]|$)/);
  return m ? Number(m[1]) : null;
}

// Query parameters that can hold what a reader typed about themselves (the no-JS /find?address=…, sign-in links).
const PRIVATE_QUERY = /^(?:address|addr|street|email|e-mail|name|phone|token|otp|code|key|sig|signature)$/i;
// Paths whose last part is a secret (ward alerts links): reported without it.
const SECRET_PATH = /^(\/alerts\/(?:confirm|manage|unsubscribe))\/.+$/;

// A page's path and query as GA4 may see them: private parameters removed, secret links cut, no #fragment.
export function cleanPath(pathAndQuery) {
  const u = new URL(String(pathAndQuery || '/'), 'https://x.invalid');
  for (const k of [...u.searchParams.keys()]) if (PRIVATE_QUERY.test(k)) u.searchParams.delete(k);
  const path = u.pathname.replace(SECRET_PATH, '$1');
  const q = u.searchParams.toString();
  return path + (q ? `?${q}` : '');
}

// Event params: short, flat, and nothing personal. Keys that name typed text are dropped, as is any value that looks
// like an email address or a street address. Strings are cut to 100 characters (GA4's limit).
const PRIVATE_KEY = /(?:^|_)(?:address|addr|street|email|name|phone|query|q|text|search_term|label|title)$/i;
const EMAIL = /[^\s@]+@[^\s@]+/;
const STREET = /\b\d{1,6}\s+(?:[nsew]\.?\s+)?[a-z0-9.' -]{2,40}\s(?:st|street|ave|avenue|blvd|boulevard|rd|road|dr|drive|pl|place|ct|court|ln|lane|pkwy|parkway|way|ter|terrace|hwy)\b/i;
export function cleanParams(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (v == null || PRIVATE_KEY.test(k)) continue;
    if (typeof v === 'number') { if (Number.isFinite(v)) out[k] = v; continue; }
    if (typeof v === 'boolean') { out[k] = v ? 'yes' : 'no'; continue; }
    const s = String(v);
    if (EMAIL.test(s) || STREET.test(s)) continue;
    out[k] = s.slice(0, 100);
  }
  return out;
}

const gtagOf = () => (typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null);

// The page the reader is on (set by pageView): every event carries its content group, and its ward when it has one.
let context = {};
let lastPage = null;

export function track(name, params = {}) {
  const g = gtagOf();
  if (!g) return null;
  const sent = cleanParams({ ...context, ...params });
  try { g('event', name, sent); } catch {}
  return sent;
}

// One page_view per page: the app calls this after each route has drawn (send_page_view is off in the GA4 config, see
// site/index.html). The same address twice in a row (a re-render) isn't a new view.
export function pageView({ path, title, origin } = {}) {
  const loc = typeof location !== 'undefined' ? location : null;
  const clean = cleanPath(path ?? (loc ? loc.pathname + loc.search : '/'));
  const group = contentGroup(clean);
  const ward = pageWard(clean);
  context = { content_group: group, ...(ward ? { ward } : {}) };
  if (clean === lastPage) return null;
  lastPage = clean;
  const g = gtagOf();
  if (!g) return null;
  const page_location = `${origin ?? loc?.origin ?? ''}${clean}`;
  const page_title = title ?? (typeof document !== 'undefined' ? document.title : undefined);
  try {
    g('set', { page_location, page_title });   // later events on this page report the cleaned address too
    const sent = { page_location, page_title, ...context };
    g('event', 'page_view', sent);
    return sent;
  } catch { return null; }
}

// User properties: signed in or not, and the ward the site keeps for this reader (their account's home ward, or the
// one they last looked up on this device). Sent again only when they change.
let lastUser = '';
export function setUser({ signedIn, homeWard } = {}) {
  const props = { signed_in: signedIn ? 'yes' : 'no', home_ward: homeWard ? String(Number(homeWard)) : 'none' };
  const key = JSON.stringify(props);
  if (key === lastUser) return null;
  lastUser = key;
  const g = gtagOf();
  if (!g) return null;
  try { g('set', 'user_properties', props); } catch {}
  return props;
}

// article_engaged buckets: how far through the article (25/50/75/100; 0 below a quarter) and how long it was read.
export const scrollBucket = (share) => [100, 75, 50, 25].find((b) => share * 100 >= b) ?? 0;
export const secondsBucket = (s) => (s < 10 ? '0-10' : s < 30 ? '10-30' : s < 60 ? '30-60' : s < 180 ? '60-180' : s < 600 ? '180-600' : '600+');

// For tests: forget the page and user last sent.
export function resetAnalytics() { context = {}; lastPage = null; lastUser = ''; }
